import { STATUSES } from '../constants/statuses';
import { MONTHS, MONTH_MAP } from '../constants/months';

export const months = MONTHS;
export { MONTH_MAP };

export const VALID_CATEGORIES = ['RET', 'PRJ', 'CPRJ'];

export const normalize = (str) => {
    return (str || '').toLowerCase().replace(/[^a-z0-9]/g, '');
};

export const safeKey = (val) => (val !== null && val !== undefined && val !== '') ? String(val).trim().toUpperCase() : 'NULL';

export const getCategoryLabel = (cat) => {
    if (!cat) return 'UNKNOWN';
    const c = cat.trim().toUpperCase();
    if (c === 'RET') return 'RETAIL';
    if (c === 'PRJ') return 'PROJECT';
    if (c === 'CPRJ') return 'CUSTOMISED PROJECT';
    return c;
};

export const buildHierarchy = (territories, companiesMap, showroomsMap, rawFactoryVendorsList, historicCatsMap = {}, showEco = false) => {
    const rows = [];
    const createdPaths = new Set();
    const factoryVendorsList = rawFactoryVendorsList.filter(v => {
        const name = (v.name || '').toUpperCase();
        if (name.includes('NOLTE ECO') && !showEco) return false;
        return true;
    });

    const activeVendorMap = {};
    factoryVendorsList.forEach(v => activeVendorMap[String(v.id)] = v.name);
    
    const VENDOR_ORDER = ['NOLTE NEO', 'NOLTE KUECHEN', 'EXPRESS KUECHEN', 'NOLTE SPA'];
    
    const getVendorRank = (name) => {
        const upperName = (name || '').toUpperCase();
        const idx = VENDOR_ORDER.findIndex(v => upperName.includes(v));
        return idx !== -1 ? idx : 99;
    };

    const vendorSortFn = (a, b) => {
        const nameA = activeVendorMap[String(a.vendorId)] || a.factoryVendor || '';
        const nameB = activeVendorMap[String(b.vendorId)] || b.factoryVendor || '';
        return getVendorRank(nameA) - getVendorRank(nameB);
    };

    const companyCatsMap = {};
    Object.keys(companiesMap).forEach(tId => {
        companiesMap[tId].forEach(comp => {
            const cats = new Set();
            const shows = showroomsMap[comp.id] || [];

            if (shows.length > 0) {
                shows.forEach(show => {
                    if (show.budgetOrderCategoryIds && Array.isArray(show.budgetOrderCategoryIds)) {
                        show.budgetOrderCategoryIds.forEach(c => cats.add(c.trim().toUpperCase()));
                    }
                    if (historicCatsMap[String(show.id)]) {
                        historicCatsMap[String(show.id)].forEach(c => cats.add(c.trim().toUpperCase()));
                    }
                });
            }

            if (comp.budgetOrderCategoryIds && Array.isArray(comp.budgetOrderCategoryIds)) {
                comp.budgetOrderCategoryIds.forEach(c => cats.add(c.trim().toUpperCase()));
            }
            if (historicCatsMap[String(comp.id)]) {
                historicCatsMap[String(comp.id)].forEach(c => cats.add(c.trim().toUpperCase()));
            }

            if (cats.size === 0) {
                cats.add('RET'); cats.add('PRJ');
            }

            companyCatsMap[comp.id] = Array.from(cats).filter(c => VALID_CATEGORIES.includes(c));
        });
    });

    const getCompanyVendors = () => factoryVendorsList.map(v => ({ vendorId: v.id, factoryVendor: v.name }));

    const createRow = (id, type, name, hierarchy, extraProps = {}) => {
        const row = { id: String(id), type, name, hierarchy, total: 0, ref_total: 0, status: STATUSES.NEW, ...extraProps };
        months.forEach(m => { row[m] = 0; row[`ref_${m}`] = 0; });
        return row;
    };

    const addIfNew = (row) => {
        const key = row.hierarchy.join('|');
        if (!createdPaths.has(key)) {
            rows.push(row);
            createdPaths.add(key);
        }
    };

    const sortedTerritories = [...territories].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));

    sortedTerritories.forEach(terr => {
        const tId = terr.id;
        const tName = terr.displayName || terr.name || 'Unknown Territory';
        const companies = companiesMap[tId] || [];
        if (companies.length === 0) return;
        
        addIfNew(createRow(tId, 'TERRITORY', tName, [tName], { territoryId: tId }));
        
        const tCats = new Set();
        companies.forEach(comp => (companyCatsMap[comp.id] || []).forEach(c => tCats.add(c)));

        Array.from(tCats).forEach(cat => {
            const catLabel = getCategoryLabel(cat);
            const tProds = new Map();
            companies.forEach(comp => {
                if ((companyCatsMap[comp.id] || []).includes(cat)) {
                    getCompanyVendors(comp).forEach(ref => tProds.set(ref.vendorId, ref));
                }
            });

            if (tProds.size > 0) {
                addIfNew(createRow(`T-${tId}-SUM-${cat}`, 'SUMMARY_CATEGORY', catLabel, [tName, catLabel], { territoryId: tId, categoryId: cat }));
                Array.from(tProds.values()).sort(vendorSortFn).forEach(ref => {
                    const pName = activeVendorMap[String(ref.vendorId)] || ref.factoryVendor || 'Unknown Brand';
                    addIfNew(createRow(`T-${tId}-SUM-${cat}-${ref.vendorId}`, 'SUMMARY_PRODUCT', pName, [tName, catLabel, pName], { territoryId: tId, categoryId: cat, productId: ref.vendorId }));
                });
            }
        });

        const countriesMap = new Map();
        companies.forEach(comp => {
            const cName = comp.countryName || 'Other';
            if (!countriesMap.has(cName)) countriesMap.set(cName, []);
            countriesMap.get(cName).push(comp);
        });

        Array.from(countriesMap.keys()).sort().forEach(cName => {
            const cPath = [tName, cName];
            const countryComps = countriesMap.get(cName);
            addIfNew(createRow(`C-${tId}-${cName}`, 'COUNTRY', cName, cPath, { territoryId: tId }));

            const cCats = new Set();
            countryComps.forEach(comp => (companyCatsMap[comp.id] || []).forEach(c => cCats.add(c)));

            Array.from(cCats).forEach(cat => {
                const catLabel = getCategoryLabel(cat);
                const cProds = new Map();
                countryComps.forEach(comp => {
                    if ((companyCatsMap[comp.id] || []).includes(cat)) {
                        getCompanyVendors(comp).forEach(ref => cProds.set(ref.vendorId, ref));
                    }
                });

                if (cProds.size > 0) {
                    addIfNew(createRow(`C-${tId}-${cName}-SUM-${cat}`, 'SUMMARY_CATEGORY', catLabel, [...cPath, catLabel], { territoryId: tId, categoryId: cat }));
                    Array.from(cProds.values()).sort(vendorSortFn).forEach(ref => {
                        const pName = activeVendorMap[String(ref.vendorId)] || ref.factoryVendor || 'Unknown Brand';
                        addIfNew(createRow(`C-${tId}-${cName}-SUM-${cat}-${ref.vendorId}`, 'SUMMARY_PRODUCT', pName, [...cPath, catLabel, pName], { territoryId: tId, categoryId: cat, productId: ref.vendorId }));
                    });
                }
            });

            const sortedPartners = [...countryComps].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));

            sortedPartners.forEach(comp => {
                const pName = comp.id ? `${comp.id} - ${comp.name}` : comp.name;
                const pPath = [...cPath, pName];
                addIfNew(createRow(`P-${tId}-${comp.id}`, 'PARTNER', pName, pPath, { territoryId: tId, companyId: comp.id }));

                const shows = showroomsMap[comp.id] || [];
                const sortedShows = [...shows].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));

                const regionsMap = new Map();
                const noRegionShows = [];

                sortedShows.forEach(show => {
                    const rName = show.region ? show.region.trim() : '';
                    if (rName) {
                        if (!regionsMap.has(rName)) regionsMap.set(rName, []);
                        regionsMap.get(rName).push(show);
                    } else {
                        noRegionShows.push(show);
                    }
                });

                let isRedundant = false;
                if (sortedShows.length === 1 && regionsMap.size === 0) {
                    const normShow = normalize(sortedShows[0].name || sortedShows[0].shortKey || '');
                    if (String(sortedShows[0].id) === String(comp.id) || normShow === normalize(comp.name) || normShow === normalize("Main Showroom") || normShow === '') {
                        isRedundant = true;
                    }
                }

                const compVendors = getCompanyVendors(comp);
                const pCats = companyCatsMap[comp.id] || [];

                if (sortedShows.length > 0 && !isRedundant) {
                    pCats.forEach(cat => {
                        const catLabel = getCategoryLabel(cat);
                        if (compVendors.length > 0) {
                            addIfNew(createRow(`P-${tId}-${comp.id}-SUM-${cat}`, 'SUMMARY_CATEGORY', catLabel, [...pPath, catLabel], { territoryId: tId, companyId: comp.id, categoryId: cat }));
                            compVendors.sort(vendorSortFn).forEach(ref => {
                                const prodName = activeVendorMap[String(ref.vendorId)] || ref.factoryVendor || 'Unknown Brand';
                                addIfNew(createRow(`P-${tId}-${comp.id}-SUM-${cat}-${ref.vendorId}`, 'SUMMARY_PRODUCT', prodName, [...pPath, catLabel, prodName], { territoryId: tId, companyId: comp.id, categoryId: cat, productId: ref.vendorId }));
                            });
                        }
                    });

                    const processShowrooms = (showsList, parentPath) => {
                        showsList.forEach(show => {
                            const baseShowName = show.name || show.shortKey || 'Showroom';
                            const sName = show.id ? `${show.id} - ${baseShowName}` : baseShowName;
                            const sPath = [...parentPath, sName];
                            addIfNew(createRow(`S-${tId}-${comp.id}-${show.id}`, 'SHOWROOM', sName, sPath, { territoryId: tId, companyId: comp.id, showroomId: show.id }));

                            let sCatsArray = (show.budgetOrderCategoryIds && Array.isArray(show.budgetOrderCategoryIds) && show.budgetOrderCategoryIds.length > 0) ? show.budgetOrderCategoryIds : pCats;
                            const finalShowCats = new Set(sCatsArray.map(c => c.trim().toUpperCase()));
                            if (historicCatsMap[String(show.id)]) historicCatsMap[String(show.id)].forEach(c => finalShowCats.add(c.trim().toUpperCase()));

                            Array.from(finalShowCats).filter(c => VALID_CATEGORIES.includes(c)).forEach(cat => {
                                const catLabel = getCategoryLabel(cat);
                                if (compVendors.length > 0) {
                                    addIfNew(createRow(`S-${tId}-${comp.id}-${show.id}-CAT-${cat}`, 'CATEGORY', catLabel, [...sPath, catLabel], { territoryId: tId, companyId: comp.id, showroomId: show.id, categoryId: cat }));
                                    compVendors.sort(vendorSortFn).forEach((ref, idx) => {
                                        const prodName = activeVendorMap[String(ref.vendorId)] || ref.factoryVendor || 'Unknown Brand';
                                        addIfNew(createRow(`S-${tId}-${comp.id}-${show.id}-${cat}-${ref.vendorId}-${idx}`, 'PRODUCT', prodName, [...sPath, catLabel, prodName], { territoryId: tId, companyId: comp.id, showroomId: show.id, categoryId: cat, productId: ref.vendorId }));
                                    });
                                }
                            });
                        });
                    };

                    regionsMap.forEach((regionShows, rName) => {
                        const rPath = [...pPath, rName];
                        addIfNew(createRow(`R-${tId}-${comp.id}-${rName}`, 'REGION', rName, rPath, { territoryId: tId, companyId: comp.id }));
                        const rCats = new Set();
                        regionShows.forEach(show => {
                            let sCatsArray = (show.budgetOrderCategoryIds && Array.isArray(show.budgetOrderCategoryIds) && show.budgetOrderCategoryIds.length > 0) ? show.budgetOrderCategoryIds : pCats;
                            sCatsArray.forEach(c => rCats.add(c.trim().toUpperCase()));
                            if (historicCatsMap[String(show.id)]) historicCatsMap[String(show.id)].forEach(c => rCats.add(c.trim().toUpperCase()));
                        });

                        Array.from(rCats).filter(c => VALID_CATEGORIES.includes(c)).forEach(cat => {
                            const catLabel = getCategoryLabel(cat);
                            if (compVendors.length > 0) {
                                addIfNew(createRow(`R-${tId}-${comp.id}-${rName}-SUM-${cat}`, 'SUMMARY_CATEGORY', catLabel, [...rPath, catLabel], { territoryId: tId, companyId: comp.id, categoryId: cat }));
                                compVendors.sort(vendorSortFn).forEach(ref => {
                                    const prodName = activeVendorMap[String(ref.vendorId)] || ref.factoryVendor || 'Unknown Brand';
                                    addIfNew(createRow(`R-${tId}-${comp.id}-${rName}-SUM-${cat}-${ref.vendorId}`, 'SUMMARY_PRODUCT', prodName, [...rPath, catLabel, prodName], { territoryId: tId, companyId: comp.id, categoryId: cat, productId: ref.vendorId }));
                                });
                            }
                        });
                        processShowrooms(regionShows, rPath);
                    });

                    processShowrooms(noRegionShows, pPath);
                } else {
                    const hiddenShowroomId = comp.id;
                    let hiddenCatsArray = [];

                    if (sortedShows.length === 1 && sortedShows[0].budgetOrderCategoryIds && sortedShows[0].budgetOrderCategoryIds.length > 0) {
                        hiddenCatsArray = sortedShows[0].budgetOrderCategoryIds;
                    } else if (comp.budgetOrderCategoryIds && comp.budgetOrderCategoryIds.length > 0) {
                        hiddenCatsArray = comp.budgetOrderCategoryIds;
                    } else {
                        hiddenCatsArray = pCats;
                    }

                    const finalHiddenCats = new Set(hiddenCatsArray.map(c => c.trim().toUpperCase()));
                    if (historicCatsMap[String(comp.id)]) historicCatsMap[String(comp.id)].forEach(c => finalHiddenCats.add(c.trim().toUpperCase()));

                    Array.from(finalHiddenCats).filter(c => VALID_CATEGORIES.includes(c)).forEach(cat => {
                        const catLabel = getCategoryLabel(cat);
                        if (compVendors.length > 0) {
                            addIfNew(createRow(`P-${tId}-${comp.id}-CAT-${cat}`, 'CATEGORY', catLabel, [...pPath, catLabel], { territoryId: tId, companyId: comp.id, showroomId: hiddenShowroomId, categoryId: cat }));
                            compVendors.sort(vendorSortFn).forEach((ref, idx) => {
                                const prodName = activeVendorMap[String(ref.vendorId)] || ref.factoryVendor || 'Unknown Brand';
                                addIfNew(createRow(`P-${tId}-${comp.id}-${cat}-${ref.vendorId}-${idx}`, 'PRODUCT', prodName, [...pPath, catLabel, prodName], { territoryId: tId, companyId: comp.id, showroomId: hiddenShowroomId, categoryId: cat, productId: ref.vendorId }));
                            });
                        }
                    });
                }
            });
        });
    });

    const territoriesWithProducts = new Set(rows.filter(r => r.type === 'PRODUCT').map(r => r.hierarchy[0]));
    return rows.filter(row => row.type === 'TERRITORY' ? territoriesWithProducts.has(row.name) : true);
};

const getAncestorPaths = (hierarchy) => {
    const h = hierarchy;
    const T = h[0], C = h[1], P = h[2], Cat = h[h.length - 2], ProdName = h[h.length - 1];
    const paths = [[T], [T, C], [T, C, P], [T, Cat], [T, Cat, ProdName], [T, C, Cat], [T, C, Cat, ProdName]];

    if (h.length === 5) {
        paths.push([T, C, P, Cat]);
    } else if (h.length === 6) {
        const S = h[3];
        paths.push([T, C, P, Cat], [T, C, P, Cat, ProdName], [T, C, P, S], [T, C, P, S, Cat]);
    } else if (h.length === 7) {
        const R = h[3], S = h[4];
        paths.push([T, C, P, Cat], [T, C, P, Cat, ProdName], [T, C, P, R], [T, C, P, R, Cat], [T, C, P, R, Cat, ProdName], [T, C, P, R, S], [T, C, P, R, S, Cat]);
    }
    return paths;
};

export const updateParentTotals = (allData) => {
    const indexByPath = new Map();
    allData.forEach((row, index) => {
        if (row.type !== 'PRODUCT') {
            months.forEach(m => { row[m] = 0; row[`ref_${m}`] = 0; });
            row.total = 0;
            row.ref_total = 0;
        }
        indexByPath.set(row.hierarchy.join('|'), index);
    });

    allData.forEach(prod => {
        if (prod.type !== 'PRODUCT') return;
        const parentIndexes = getAncestorPaths(prod.hierarchy)
            .map(path => indexByPath.get(path.join('|')))
            .filter(index => index !== undefined);
        prod.parentIndexes = parentIndexes;

        parentIndexes.forEach(index => {
            const node = allData[index];
            months.forEach(m => {
                node[m] += (prod[m] || 0);
                node[`ref_${m}`] += (prod[`ref_${m}`] || 0);
            });
            node.total += (prod.total || 0);
            node.ref_total += (prod.ref_total || 0);
        });
    });
};

export const captureRowValues = (row) => {
    const values = { total: Number(row.total) || 0 };
    months.forEach(m => { values[m] = Number(row[m]) || 0; });
    return values;
};

export const applyRowDeltaToParents = (allData, row, before) => {
    if (!Array.isArray(row.parentIndexes)) return false;
    const diff = {};
    let hasChange = false;
    months.forEach(m => {
        diff[m] = (Number(row[m]) || 0) - (Number(before[m]) || 0);
        if (diff[m] !== 0) hasChange = true;
    });
    const totalDiff = (Number(row.total) || 0) - (Number(before.total) || 0);
    if (!hasChange && totalDiff === 0) return true;

    row.parentIndexes.forEach(index => {
        const node = allData[index];
        if (!node) return;
        months.forEach(m => {
            if (diff[m] !== 0) node[m] += diff[m];
        });
        node.total += totalDiff;
    });
    return true;
};
